<script lang="ts" setup>
import AlertMessage from "@/components/AlertMessage.vue";
import DetachTruckDialog from "@/views/trips/edit/dialogs/DetachTruckDialog.vue";
import OmniDialog from "@/components/OmniDialog.vue";
import type { TripOrder, TripRoute } from "@/views/trips/edit/trip";
import { computed, ref } from "vue";
import { RouteRedispatchState } from "@/enums/trip";
import { RouteStatus } from "@/data/trip.js";
import { PlannedRouteMessage } from "@/data/route.js";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import DispatchDialog from "@/views/trips/edit/dialogs/DispatchDialog.vue";
import CancelledChip from "@/components/chips/CancelledChip.vue";
import PlannedChip from "@/components/chips/PlannedChip.vue";
import AttachTruckDialog from "@/views/trips/edit/dialogs/AttachTruckDialog.vue";
import TooltipButton from "@/components/controls/TooltipButton.vue";
import type { DisabledRule } from "@/types/validation";
import { useDisableRules } from "@/composables/reactive/useDisableRules";
import { OrderStopType } from "@/data/order";

const route = defineModel<TripRoute | null>({ default: null });

const { createTripDispatchDisableRules } = useDisableRules();

withDefaults(
  defineProps<{
    canEdit?: boolean;
    orders: TripOrder[];
  }>(),
  {
    canEdit: false,
  },
);

const emits = defineEmits<{
  (e: "update:modelValue", value: TripRoute): void;
  (e: "attach", value: TruckDto): void;
  (e: "removeTruck"): void;
  (e: "removeRoute"): void;
}>();

const attachDialog = ref(false);
const showDispatchDialog = ref(false);
const removeTruckDialog = ref(false);
const removeRouteDialog = ref(false);

const dispatchValidationRules = computed<DisabledRule[]>(() => {
  return route.value ? createTripDispatchDisableRules(route.value) : [];
});

const needRedispatch = computed(() => {
  return route.value ? route.value.redispatch === RouteRedispatchState.needed && !route.value.isDispatched : false;
});

const isPlanned = computed(() => {
  return route.value?.status === RouteStatus.planned;
});

const isCancelled = computed(() => {
  return route.value?.status === RouteStatus.cancelled;
});

const tripBlockClass = computed(() => {
  if (isCancelled.value) {
    return "bg-red-lighten-5";
  }

  return "bg-white";
});

const truckNumberClass = computed(() => {
  if (isCancelled.value) {
    return "";
  }

  return "bg-grey-lighten-4";
});

const firstActivePickUp = computed(() => {
  return route.value?.routeStops.find((routeStop) => routeStop.type === OrderStopType.pickup && routeStop.use);
});

const firstActivePickUpFacilityCoordinates = computed(() => {
  return firstActivePickUp.value?.facility?.addressCoordinates || null;
});

function attach(value: TruckDto) {
  attachDialog.value = false;
  emits("attach", value);
}

function removeTruck(value: boolean) {
  removeTruckDialog.value = value;
  emits("removeTruck");
}
</script>

<template>
  <div v-if="route" :class="tripBlockClass" class="trip-block" data-qa="route-block">
    <div :class="{ 'border-warning': needRedispatch }" class="trip-block__inner pa-4 rounded-lg">
      <div class="trip-block__header d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <span class="text-grey-darken-2 font-weight-medium">Route</span>
          <planned-chip v-if="isPlanned" class="ml-3" />
          <cancelled-chip v-if="isCancelled" class="ml-3" />
        </div>

        <v-menu v-if="canEdit" offset-y>
          <template #activator="{ props: menuProps }">
            <v-btn elevation="0" icon="mdi-dots-horizontal" size="x-small" v-bind="menuProps" />
          </template>
          <v-list>
            <v-tooltip :disabled="!route.id" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <v-list-item
                    :disabled="Boolean(route.id)"
                    class="cursor-pointer"
                    v-bind="tooltipProps"
                    @click="removeRouteDialog = true"
                  >
                    <v-list-item-title>Remove route</v-list-item-title>
                  </v-list-item>
                </div>
              </template>

              <span>This route is already saved in the system and can`t be removed</span>
            </v-tooltip>
          </v-list>
        </v-menu>
      </div>
      <div
        v-if="route?.truck"
        :class="truckNumberClass"
        class="d-flex align-center border-grey-lighter-1 justify-space-between pa-4 rounded"
      >
        <span class="font-weight-medium text-primary" data-qa="truck-number">Truck #{{ route.truck.number }}</span>
      </div>
      <tooltip-button :rules="dispatchValidationRules">
        <template #default="{ disabled }">
          <v-btn
            v-if="route?.truck"
            :disabled="disabled"
            :variant="route.isDispatched ? 'outlined' : 'flat'"
            block
            class="btn text-uppercase mt-3"
            color="primary"
            @click="showDispatchDialog = true"
          >
            <template v-if="!route.isDispatched">Dispatch</template>
            <template v-else-if="isCancelled">View settings</template>
            <template v-else>Dispatch settings</template>
          </v-btn>

          <v-btn
            v-else
            block
            class="text-uppercase font-weight-600"
            color="primary"
            variant="outlined"
            @click="attachDialog = true"
          >
            Attach truck/Carrier
          </v-btn>
        </template>
      </tooltip-button>
      <alert-message v-if="needRedispatch" class="mt-3"> Please update information for driver</alert-message>
      <alert-message v-if="isPlanned" bg-color="indigo-lighten-5" border-color="none" class="mt-3" icon-color="indigo">
        <span class="text-indigo font-weight-500">
          {{ PlannedRouteMessage }}
        </span>
      </alert-message>
    </div>

    <omni-dialog v-model="attachDialog" title="Attach truck" width="850">
      <attach-truck-dialog :coordinates="firstActivePickUpFacilityCoordinates" @attach="attach" />
    </omni-dialog>

    <omni-dialog
      v-model="removeTruckDialog"
      :title="'Remove truck #' + route?.truck?.number"
      primary-action-label="Remove"
      width="360"
      @primary-action:click="removeTruck"
    >
      <detach-truck-dialog v-if="route?.truck" :truck="route.truck" />
    </omni-dialog>

    <dispatch-dialog v-model="showDispatchDialog" v-model:route="route" :orders="orders" />

    <omni-dialog
      v-model="removeRouteDialog"
      primary-action-label="Remove"
      title="Remove route"
      width="360"
      @primary-action:click="$emit('removeRoute')"
    >
      <div class="text-grey-darken-1 py-4">Are you sure want to remove from trip?</div>
    </omni-dialog>
  </div>
</template>

<style scoped>
.trip-block__inner {
  min-height: 180px;
}
</style>
